import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import messages from './data/ServiceMessages.json';
import { CATEGORY_META, fmt, getSummary, ActionCountdownBadge } from './shared';
import Layout from './Layout';
import ShareButtons from './ShareButtons';
import { getServiceTagStyle } from './serviceTagColors';

const PAGE_SIZE = 50;

// ── MessageRow ────────────────────────────────────────────────────────────────

function MessageRow({ message }) {
  const catMeta     = CATEGORY_META[message.category] ?? { label: message.category, cls: 'bg-gray-100 text-gray-700 border-gray-200' };
  const updatedDate = fmt(message.lastModifiedDateTime ?? message.startDateTime);

  return (
    <li className="border-b border-gray-300 dark:border-gray-700 last:border-b-0">
      <Link
        to={`/${message.id}`}
        className="group flex items-center gap-3 bg-white px-4 py-3.5 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 dark:bg-gray-900 dark:hover:bg-gray-800 sm:px-5"
      >
        <span className="shrink-0 bg-gray-100 px-2 py-0.5 font-mono text-xs font-semibold text-gray-500 dark:bg-gray-800 dark:text-gray-400">
          {message.id}
        </span>

        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium leading-5 text-gray-900 transition-colors group-hover:text-blue-700 dark:text-gray-100 dark:group-hover:text-blue-400 sm:text-base">
            {message.title}
          </span>

          <span className="mt-1.5 flex items-center gap-2 overflow-hidden whitespace-nowrap">
            <span className={`shrink-0 border px-2 py-0.5 text-xs font-medium ${catMeta.cls}`}>
              {catMeta.label}
            </span>
            {message.isMajorChange && (
              <span className="hidden shrink-0 border border-purple-200 bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-800 sm:inline">
                ★ Major
              </span>
            )}
            {message.actionRequiredByDateTime && (
              <span className="hidden shrink-0 bg-red-600 px-2 py-0.5 text-xs font-semibold text-white sm:inline">
                Action required
              </span>
            )}
            {message.actionRequiredByDateTime && (
              <span className="hidden shrink-0 md:inline">
                <ActionCountdownBadge dateStr={message.actionRequiredByDateTime} />
              </span>
            )}
            <span className="hidden items-center gap-1 lg:flex">
              {message.services?.slice(0, 2).map(svc => (
                <span
                  key={svc}
                  className="rounded-full border px-2 py-0.5 text-xs font-medium"
                  style={getServiceTagStyle(svc)}
                >
                  {svc}
                </span>
              ))}
              {(message.services?.length ?? 0) > 2 && (
                <span className="text-xs text-gray-500 dark:text-gray-400">+{message.services.length - 2}</span>
              )}
            </span>
          </span>
        </span>

        <span className="w-20 shrink-0 whitespace-nowrap text-right text-xs text-gray-500 dark:text-gray-400 sm:w-24">
          {updatedDate}
        </span>

        <svg className="h-5 w-5 shrink-0 text-gray-400 transition-colors group-hover:text-blue-500"
          fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </Link>
    </li>
  );
}

// ── MessageList page ──────────────────────────────────────────────────────────

export default function MessageList() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive all filter state from URL params — makes filters shareable via URL
  const search         = searchParams.get('q') ?? '';
  const categoryFilter = searchParams.get('cat') ?? 'all';
  const serviceFilter  = searchParams.get('svc') ?? 'all';
  const tagFilter      = searchParams.get('tag') ?? 'all';
  const majorOnly      = searchParams.get('major') === '1';
  const actionOnly     = searchParams.get('action') === '1';
  const sortOrder      = searchParams.get('sort') ?? 'newest';
  const page           = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10));

  function updateFilter(updates) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(updates)) {
        if (v === null || v === undefined || v === '') next.delete(k);
        else next.set(k, String(v));
      }
      if (!('page' in updates)) next.delete('page');
      return next;
    }, { replace: true });
  }

  const setSearch     = val => updateFilter({ q: val || null });
  const setCategory   = val => updateFilter({ cat: val === 'all' ? null : val });
  const setService    = val => updateFilter({ svc: val === 'all' ? null : val });
  const setTag        = val => updateFilter({ tag: val === 'all' ? null : val });
  const setMajorOnly  = val => updateFilter({ major: val ? '1' : null });
  const setActionOnly = val => updateFilter({ action: val ? '1' : null });
  const setSortOrder  = val => updateFilter({ sort: val === 'newest' ? null : val });
  function setPage(fn) {
    const newPage = typeof fn === 'function' ? fn(page) : fn;
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (newPage <= 1) next.delete('page'); else next.set('page', String(newPage));
      return next;
    }, { replace: true });
  }

  const allServices = useMemo(() => {
    const set = new Set();
    messages.forEach(m => m.services?.forEach(s => set.add(s)));
    return [...set].sort();
  }, []);

  const allTags = useMemo(() => {
    const set = new Set();
    messages.forEach(m => m.tags?.forEach(t => set.add(t)));
    return [...set].sort();
  }, []);

  const filtered = useMemo(() => {
    let result = messages;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(m => {
        const summary = getSummary(m) ?? '';
        return (
          m.title?.toLowerCase().includes(q) ||
          m.id?.toLowerCase().includes(q) ||
          summary.toLowerCase().includes(q) ||
          m.tags?.some(t => t.toLowerCase().includes(q)) ||
          m.services?.some(s => s.toLowerCase().includes(q))
        );
      });
    }
    if (categoryFilter !== 'all') result = result.filter(m => m.category === categoryFilter);
    if (serviceFilter !== 'all')  result = result.filter(m => m.services?.includes(serviceFilter));
    if (tagFilter !== 'all')      result = result.filter(m => m.tags?.includes(tagFilter));
    if (majorOnly)                result = result.filter(m => m.isMajorChange);
    if (actionOnly)               result = result.filter(m => !!m.actionRequiredByDateTime);
    return [...result].sort((a, b) => {
      const da = new Date(a.lastModifiedDateTime ?? a.startDateTime);
      const db = new Date(b.lastModifiedDateTime ?? b.startDateTime);
      return sortOrder === 'newest' ? db - da : da - db;
    });
  }, [search, categoryFilter, serviceFilter, tagFilter, majorOnly, actionOnly, sortOrder]);

const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  const hasFilters = search || categoryFilter !== 'all' || serviceFilter !== 'all' ||
                     tagFilter !== 'all' || majorOnly || actionOnly;

  function resetFilters() { setSearchParams({}, { replace: true }); }

  return (
    <Layout>
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6">

        {/* Welcome */}
        <div className="mb-6 px-1">
          <h1 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">Welcome to MS Message Center</h1>
          <p className="text-base text-gray-600 dark:text-gray-300 leading-relaxed">
            A community-built viewer for Microsoft 365 Message Center announcements, helping you stay on top of
            service changes, planned updates, and action-required notices across the Microsoft cloud.
          </p>
          <p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed mt-1">
            This is a personal project by Microsoft MVP{' '}
            <a
              href="https://www.linkedin.com/in/danielbradley2/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700 hover:underline"
            >
              Daniel Bradley
            </a>
            , not affiliated with Microsoft in any way. For more Microsoft 365 content, check out{' '}
            <a
              href="https://ourcloudnetwork.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700 hover:underline"
            >
              ourcloudnetwork.com
            </a>
            .
          </p>
          <div className="mt-4">
            <ShareButtons
              url="https://msmessagecenter.com/"
              text="MS Message Center – a free community viewer for Microsoft 365 announcements and service updates."
            />
          </div>
        </div>

        {/* Search & Filters */}
        <section aria-label="Search and filter messages" className="mb-8 bg-white p-5 dark:bg-gray-900">
          <div className="relative mb-3">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
              fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="search"
              aria-label="Search messages"
              placeholder="Search by title, ID, summary, service or tag…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-9 py-2.5 border border-gray-300 dark:border-gray-600 rounded-none text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500"
            />
            {search && (
              <button onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 leading-none">
                ✕
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <select aria-label="Filter by category" value={categoryFilter} onChange={e => setCategory(e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-none px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="all">All Categories</option>
              {Object.entries(CATEGORY_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <select aria-label="Filter by service" value={serviceFilter} onChange={e => setService(e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-none px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="all">All Services</option>
              {allServices.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select aria-label="Filter by tag" value={tagFilter} onChange={e => setTag(e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-none px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="all">All Tags</option>
              {allTags.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select aria-label="Sort order" value={sortOrder} onChange={e => setSortOrder(e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-none px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
            </select>
            <button
              onClick={() => setMajorOnly(!majorOnly)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors select-none ${
                majorOnly
                  ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-purple-400 hover:text-purple-700 dark:hover:text-purple-400'
              }`}
            >
              <span className="text-base leading-none">&#9733;</span> Major only
            </button>
            <button
              onClick={() => setActionOnly(!actionOnly)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors select-none ${
                actionOnly
                  ? 'bg-red-600 text-white border-red-600 shadow-sm'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-red-400 hover:text-red-700 dark:hover:text-red-400'
              }`}
            >
              <span className="text-base leading-none">&#9889;</span> Action required
            </button>
            <div className="ml-auto flex items-center gap-3">
              {hasFilters && (
                <button onClick={resetFilters} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 underline">
                  Clear filters
                </button>
              )}
              <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                {filtered.length.toLocaleString()} {filtered.length === 1 ? 'result' : 'results'}
              </span>
            </div>
          </div>
        </section>

        {/* List */}
        {filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <svg className="w-14 h-14 mx-auto mb-4 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg font-semibold text-gray-500 mb-1">No messages found</p>
            <p className="text-sm">Try different search terms or clear your filters.</p>
            {hasFilters && (
              <button onClick={resetFilters} className="mt-4 text-sm text-blue-600 hover:underline">
                Clear all filters
              </button>
            )}
          </div>
        ) : (
          <>
            <ul className="list-none overflow-hidden border border-gray-300 bg-white dark:border-gray-700 dark:bg-gray-900">
              {pageItems.map(message => (
                <MessageRow key={message.id} message={message} />
              ))}
            </ul>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Showing <span className="font-semibold text-gray-700 dark:text-gray-200">{pageStart + 1}</span>–
                  <span className="font-semibold text-gray-700 dark:text-gray-200">{Math.min(pageStart + PAGE_SIZE, filtered.length)}</span>
                  {' '}of <span className="font-semibold text-gray-700 dark:text-gray-200">{filtered.length.toLocaleString()}</span>
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 text-sm font-medium rounded-none border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  {getPageNumbers(currentPage, totalPages).map((p, idx) =>
                    p === '…' ? (
                      <span key={`gap-${idx}`} className="px-2 text-gray-400 select-none">…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`min-w-[2.25rem] px-2 py-1.5 text-sm font-medium border transition-colors ${
                          p === currentPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 text-sm font-medium rounded-none border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

// Compact pagination: shows first, last, current ±1, with ellipses
function getPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const sorted = [...pages].filter(p => p >= 1 && p <= total).sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    result.push(sorted[i]);
    if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) result.push('…');
  }
  return result;
}
